/**
 * IconSizeHelper - Utility to automatically detect image dimensions
 * Used for Leaflet marker icons to ensure proper sizing
 */

/** @type {Map<string, Promise<L.Icon|null>>} */
const iconCache = new Map();

function iconCacheKey(iconUrl, shadowUrl) {
  return shadowUrl ? `${iconUrl}\0${shadowUrl}` : iconUrl;
}

/**
 * Normalizes the return value of a marker icon resolver function.
 * @param {string|{iconUrl: string, shadowUrl?: string}|null|undefined} result
 * @returns {{iconUrl: string|null, shadowUrl: string|null}}
 */
export function parseMarkerIconResolverResult(result) {
  if (!result) {
    return { iconUrl: null, shadowUrl: null };
  }
  if (typeof result === "string") {
    return { iconUrl: result, shadowUrl: null };
  }
  if (typeof result === "object" && result.iconUrl) {
    return {
      iconUrl: result.iconUrl,
      shadowUrl: result.shadowUrl ?? null,
    };
  }
  return { iconUrl: null, shadowUrl: null };
}

export async function getImageDimensions(imageUrl) {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      // Return null on error - let Leaflet handle default behavior
      resolve(null);
    };

    img.src = imageUrl;
  });
}

/**
 * Creates a Leaflet icon with auto-detected dimensions
 * @param {string} iconUrl - URL of the icon image
 * @param {string} shadowUrl - URL of the shadow image (optional)
 * @param {object} debug - Debug logger (optional)
 * @returns {L.Icon|null} Leaflet icon object or null if iconUrl is not provided
 */
export async function createIconWithDimensions(
  iconUrl,
  shadowUrl = null,
  debug = null
) {
  if (!iconUrl) {
    return null;
  }

  const cacheKey = iconCacheKey(iconUrl, shadowUrl);
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }

  const iconPromise = buildIconWithDimensions(iconUrl, shadowUrl, debug);
  iconCache.set(cacheKey, iconPromise);
  return iconPromise;
}

async function buildIconWithDimensions(iconUrl, shadowUrl = null, debug = null) {
  // Auto-detect icon dimensions
  const iconDimensions = await getImageDimensions(iconUrl);

  // Auto-detect shadow dimensions if shadow URL provided
  let shadowDimensions = null;
  if (shadowUrl) {
    shadowDimensions = await getImageDimensions(shadowUrl);
  }

  // Build icon configuration
  const iconConfig = {
    iconUrl: iconUrl,
  };

  if (iconDimensions) {
    iconConfig.iconSize = [iconDimensions.width, iconDimensions.height];
    // Icon anchor: center-bottom of icon (where marker point touches map)
    iconConfig.iconAnchor = [iconDimensions.width / 2, iconDimensions.height];
  }

  if (shadowUrl) {
    iconConfig.shadowUrl = shadowUrl;
    if (shadowDimensions) {
      iconConfig.shadowSize = [shadowDimensions.width, shadowDimensions.height];
      // Shadow anchor: center-bottom with slight offset for visual depth
      // Offset shadow 2px right and 2px down for typical shadow effect
      iconConfig.shadowAnchor = [
        shadowDimensions.width / 2 + 2,
        shadowDimensions.height + 2
      ];
    }
  }

  // Create and return icon
  const icon = L.icon(iconConfig);

  // Debug logging
  if (debug) {
    if (iconDimensions) {
      debug.log(
        "Icon dimensions detected:",
        iconDimensions.width,
        "x",
        iconDimensions.height
      );
    } else {
      debug.warn("Could not detect icon dimensions, using natural size");
    }

    if (shadowUrl) {
      if (shadowDimensions) {
        debug.log(
          "Shadow dimensions detected:",
          shadowDimensions.width,
          "x",
          shadowDimensions.height
        );
        if (iconConfig.shadowAnchor) {
          debug.log(
            "Shadow anchor:",
            iconConfig.shadowAnchor,
            "(offset for visual depth)"
          );
        }
      } else {
        debug.warn("Could not detect shadow dimensions, using natural size");
      }
    }
    
    if (iconConfig.iconAnchor) {
      debug.log("Icon anchor:", iconConfig.iconAnchor, "(center-bottom)");
    }
  }

  return icon;
}
